import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ChevronLeft, ShoppingBag, Check, X, Clock, Package, Loader2, FileText
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../App";
import apiClient from "../lib/api";
import { toastApiError } from "../lib/toastApiError";
import CnomUsurpationNotice from "../components/CnomUsurpationNotice";
import PurchasePaymentModal from "../components/PurchasePaymentModal";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_97109381-3560-4331-a0e4-28136ead7f0e/artifacts/z6ijff3c_logo1.png";

const PurchasesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation("client");
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentPurchase, setPaymentPurchase] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const locale = i18n.language === "en" ? "en-US" : "fr-FR";

  useEffect(() => {
    if (!user?.role) return;
    fetchPurchases();
  }, [user?.role]);

  const fetchPurchases = async () => {
    try {
      const endpoint = user.role === "client"
        ? "/purchases/client"
        : "/purchases/pharmacy";
      const response = await apiClient.get(endpoint);
      setPurchases(response.data);
    } catch (error) {
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const validatePurchase = async (purchaseId, approved) => {
    try {
      await apiClient.put(`/purchases/${purchaseId}/validate`, {
        approved,
        note: approved ? t("medicineReleased") : t("purchaseRejected")
      });
      toast.success(approved ? t("medicineReleased") : t("purchaseRejected"));
      fetchPurchases();
    } catch (error) {
      toastApiError(error);
    }
  };

  const completePurchase = async (purchaseId) => {
    try {
      await apiClient.put(`/purchases/${purchaseId}/complete`, {});
      toast.success(t("purchaseCompleted"));
      fetchPurchases();
    } catch (error) {
      toastApiError(error);
    }
  };

  const deliveryStatusLabels = {
    requested: t("deliveryRequested"),
    awaiting_pickup: t("deliveryAwaitingPickup"),
    assigned: t("deliveryAssigned"),
    picked_up: t("deliveryPickedUp"),
    delivered: t("deliveryDelivered"),
  };

  const getStatusBadge = (purchase) => {
    const status = purchase.status;
    if (status === "pending" && purchase.requires_cnom_release && !purchase.can_release) {
      return <Badge className="bg-amber-100 text-amber-800">{t("statusAwaitingCnom")}</Badge>;
    }
    const statusConfig = {
      pending_payment: { label: t("statusPaymentRequired"), className: "bg-orange-100 text-orange-800" },
      pending: { label: t("statusPendingPharmacy"), className: "bg-amber-100 text-amber-700" },
      approved: { label: t("statusReleasedPaid"), className: "bg-green-100 text-green-700" },
      rejected: { label: t("statusRejected"), className: "bg-red-100 text-red-700" },
      completed: { label: t("statusCompleted"), className: "bg-blue-100 text-blue-700" }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-omu-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 h-16">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Link to="/">
              <img src={LOGO_URL} alt="OMU" className="h-12 w-auto" />
            </Link>
            <h1 className="font-heading font-semibold text-slate-900">
              {user.role === "client" ? t("myPurchasesTitle") : t("purchaseRequestsTitle")}
            </h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {purchases.length === 0 ? (
          <Card className="border-0 shadow-soft">
            <CardContent className="py-12 text-center">
              <ShoppingBag className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <h3 className="font-heading text-lg font-semibold text-slate-900">
                {t("noPurchases")}
              </h3>
              <p className="text-slate-500 mt-2">
                {user.role === "client"
                  ? t("noClientPurchases")
                  : t("noPharmacyRequests")
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {purchases.map((purchase) => (
              <Card key={purchase.id} className="border-0 shadow-soft">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-heading font-semibold text-slate-900">
                          {purchase.medicine_name}
                        </h3>
                        {getStatusBadge(purchase)}
                        {purchase.requires_cnom_release && (
                          <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700 text-xs">
                            <FileText className="h-3 w-3 mr-1" />
                            {t("prescriptionBadge")}
                          </Badge>
                        )}
                      </div>

                      <div className="space-y-1 text-sm text-slate-600">
                        {user.role === "client" ? (
                          <p>{t("pharmacyLabel")} <span className="font-medium">{purchase.pharmacy_name}</span></p>
                        ) : (
                          <p>{t("clientLabel")} <span className="font-medium">{purchase.client_name}</span> ({purchase.client_phone})</p>
                        )}
                        <p>{t("quantityLabel")} <span className="font-medium">{purchase.quantity} {t("units")}</span></p>
                        {purchase.delivery_requested && (
                          <p className="text-omu-blue-600">
                            {t("omuDelivery")}{purchase.delivery_zone_name ? ` — ${purchase.delivery_zone_name}` : ""} — {purchase.delivery_address || t("addressToConfirm")}
                          </p>
                        )}
                        {purchase.delivery_status && (
                          <p className="text-xs">
                            {t("deliveryTracking")} {deliveryStatusLabels[purchase.delivery_status] || purchase.delivery_status}
                          </p>
                        )}
                        {purchase.requires_cnom_release && purchase.status === "pending" && (
                          <p className={`text-xs ${purchase.can_release ? "text-green-700" : "text-amber-700"}`}>
                            {purchase.can_release
                              ? (user.role === "pharmacy"
                                  ? t("cnomValidatedPharmacy")
                                  : t("cnomValidatedClient"))
                              : (user.role === "pharmacy"
                                  ? (purchase.release_block_reason || t("cnomBlockedPharmacy"))
                                  : (!purchase.prescription_status
                                      ? t("cnomScanClient")
                                      : t("cnomPendingClient")))}
                            {purchase.prescription_cnom ? ` CNOM ${purchase.prescription_cnom}.` : ""}
                          </p>
                        )}
                        {purchase.unit_price > 0 && (
                          <p>{t("productsLabel")} <span className="font-medium">{purchase.total_price?.toLocaleString()} CDF</span></p>
                        )}
                        {purchase.delivery_requested && (purchase.delivery_fee > 0 || purchase.grand_total) && (
                          <>
                            <p>{t("deliveryLabel")} <span className="font-medium">{(purchase.delivery_fee || 0).toLocaleString()} CDF</span></p>
                            <p>{t("totalLabel")} <span className="font-medium">{(purchase.grand_total || ((purchase.total_price || 0) + (purchase.delivery_fee || 0))).toLocaleString()} CDF</span></p>
                          </>
                        )}
                        <p className="text-xs text-slate-400">
                          <Clock className="inline h-3 w-3 mr-1" />
                          {formatDate(purchase.created_at)}
                        </p>
                      </div>
                    </div>

                    {user.role === "pharmacy" && purchase.status === "pending" && (
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-500 hover:bg-green-600 rounded-full"
                            disabled={purchase.requires_cnom_release && !purchase.can_release}
                            title={
                              purchase.requires_cnom_release && !purchase.can_release
                                ? (purchase.release_block_reason || t("statusAwaitingCnom"))
                                : undefined
                            }
                            onClick={() => validatePurchase(purchase.id, true)}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            {purchase.delivery_requested ? t("releasePaidDelivery") : t("release")}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-500 border-red-200 hover:bg-red-50 rounded-full"
                            onClick={() => validatePurchase(purchase.id, false)}
                          >
                            <X className="h-4 w-4 mr-1" />
                            {t("refuse")}
                          </Button>
                        </div>
                        {purchase.requires_cnom_release && !purchase.can_release && (
                          <p className="text-xs text-amber-700 max-w-[220px] text-right">
                            {t("releaseBlockedCnom")}
                          </p>
                        )}
                      </div>
                    )}

                    {user.role === "client" && purchase.status === "pending_payment" && purchase.payment_status === "submitted" && (
                      <Badge className="bg-blue-100 text-blue-700">{t("paymentVerifying")}</Badge>
                    )}
                    {user.role === "client" && purchase.status === "pending_payment" && purchase.payment_status !== "submitted" && (
                      <Button
                        size="sm"
                        className="bg-omu-blue-500 hover:bg-omu-blue-600 rounded-full"
                        onClick={() => {
                          setPaymentPurchase(purchase);
                          setShowPaymentModal(true);
                        }}
                      >
                        {t("payNow")}
                      </Button>
                    )}

                    {user.role === "pharmacy" && purchase.status === "approved" && !purchase.delivery_requested && (
                      <Button
                        size="sm"
                        className="bg-omu-blue-500 hover:bg-omu-blue-600 rounded-full"
                        onClick={() => completePurchase(purchase.id)}
                      >
                        <Package className="h-4 w-4 mr-1" />
                        {t("markPickedUp")}
                      </Button>
                    )}
                  </div>

                  {purchase.requires_cnom_release && purchase.status === "pending" && purchase.can_release && (
                    <div className="mt-3">
                      <CnomUsurpationNotice />
                    </div>
                  )}

                  {purchase.validation_note && (
                    <p className="mt-3 text-sm text-slate-500 italic">
                      {t("validationNote")} {purchase.validation_note}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <PurchasePaymentModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        purchase={paymentPurchase}
        onSuccess={fetchPurchases}
      />
    </div>
  );
};

export default PurchasesPage;
